import { memo, useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Trash2, Play } from 'lucide-react'
import type { DragEvent } from 'react';
import type { StoryboardSegment } from '../../types';
import SegmentStatusChip from './SegmentStatusChip';
import { useStoryboard } from '../../context';

interface SegmentCardProps {
  segment: StoryboardSegment;
  index: number;
  selected: boolean;
  mutating: boolean;
  draggableEnabled: boolean;
  isDragging: boolean;
  dropIndicator?: 'before' | 'after' | null;
  hasActiveDrag: boolean;
  onSelect: (segmentId: string, anchor: HTMLElement) => void;
  onDelete: (segmentId: string) => void;
  onDragStart: (event: DragEvent<HTMLElement>, segmentId: string) => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent<HTMLElement>, segmentId: string) => void;
  onDragLeave: (event: DragEvent<HTMLElement>, segmentId: string) => void;
  onDrop: (event: DragEvent<HTMLElement>, segmentId: string) => void;
}

const SegmentCard: React.FC<SegmentCardProps> = memo(
  ({
    segment,
    index,
    selected,
    mutating,
    draggableEnabled,
    isDragging,
    dropIndicator = null,
    hasActiveDrag,
    onSelect,
    onDelete,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDragLeave,
    onDrop,
  }) => {
    const { updateSegment } = useStoryboard();
    const title = segment.title || `单元 ${index + 1}`;
    const prompt =
      segment.prompt && segment.prompt.trim().length
        ? segment.prompt
        : '尚未配置提示词';
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(title);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const skipBlurCommitRef = useRef(false);
    const savingRef = useRef(false);

    useEffect(() => {
      if (!isEditing) return;
      const frame = requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
      return () => cancelAnimationFrame(frame);
    }, [isEditing]);

    useEffect(() => {
      if (isEditing) return;
      setEditValue(title);
    }, [title, isEditing]);

    const handleCommitEdit = async () => {
      if (savingRef.current) return;
      const trimmed = editValue.trim();
      setIsEditing(false);
      if (!trimmed || trimmed === title) {
        skipBlurCommitRef.current = false;
        setEditValue(trimmed || title);
        return;
      }
      savingRef.current = true;
      try {
        await updateSegment(segment.segmentId, { title: trimmed });
      } catch (err) {
        console.error(err);
      } finally {
        savingRef.current = false;
        skipBlurCommitRef.current = false;
      }
    };

    const handleCancelEdit = () => {
      skipBlurCommitRef.current = true;
      setIsEditing(false);
      setEditValue(title);
    };

    return (
      <div
        role="button"
        tabIndex={0}
        draggable={draggableEnabled && !isEditing}
        onDragStart={(event) => {
          if (isEditing) {
            event.preventDefault()
            return
          }
          onDragStart(event, segment.segmentId)
        }}
        onDragEnd={onDragEnd}
        onDragOver={(event) => {
          if (isEditing) {
            event.preventDefault()
            return
          }
          onDragOver(event, segment.segmentId)
        }}
        onDragLeave={(event) => {
          if (isEditing) {
            event.preventDefault()
            return
          }
          onDragLeave(event, segment.segmentId)
        }}
        onDrop={(event) => {
          if (isEditing) {
            event.preventDefault()
            return
          }
          onDrop(event, segment.segmentId)
        }}
        onClick={(event) => {
          if (isEditing) {
            event.preventDefault()
            return
          }
          if (hasActiveDrag) {
            event.preventDefault()
            return
          }
          onSelect(segment.segmentId, event.currentTarget as HTMLElement)
        }}
        onKeyDown={(event) => {
          if (isEditing) {
            event.preventDefault()
            return
          }
          if (hasActiveDrag) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onSelect(segment.segmentId, event.currentTarget as HTMLElement)
          }
        }}
        className={[
          'flex-none w-[240px] h-[80px] rounded-lg relative',
          'bg-muted dark:bg-card/50',
          'border border-border',
          selected
            ? 'border-2 border-primary shadow-[0_0_12px_rgba(var(--primary),0.4)]'
            : '',
          'border-l-4 border-primary',
          'flex flex-col justify-between px-3 pr-8 py-2',
          draggableEnabled ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-pointer',
          isDragging ? 'opacity-50 border-dashed' : '',
          'hover:border-primary/60 hover:scale-[1.02]',
          'transition-all duration-200',
        ].join(' ')}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Play className={['size-4', selected ? 'text-primary' : 'opacity-70'].join(' ')} />
          <div
            className="flex-1 min-w-0"
            onDoubleClick={(event) => {
              event.stopPropagation()
              if (isEditing) return
              setEditValue(title)
              setIsEditing(true)
            }}
          >
            {isEditing ? (
              <Input
                ref={inputRef}
                value={editValue}
                onChange={(event) => setEditValue(event.target.value)}
                className="h-7 text-sm"
                onClick={(event) => event.stopPropagation()}
                onBlur={() => {
                  if (skipBlurCommitRef.current) {
                    skipBlurCommitRef.current = false
                    return
                  }
                  void handleCommitEdit()
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    skipBlurCommitRef.current = true
                    void handleCommitEdit()
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault()
                    handleCancelEdit()
                  }
                }}
              />
            ) : (
              <div
                className={['text-sm truncate', selected ? 'font-semibold' : 'font-medium'].join(' ')}
              >
                {title}
              </div>
            )}
          </div>
          <div className="flex-shrink-0">
            <SegmentStatusChip segment={segment} />
          </div>
        </div>
        <div className="text-xs text-muted-foreground line-clamp-2">{prompt}</div>
        <div className="absolute top-1 right-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="inline-flex items-center justify-center size-7 rounded-sm hover:bg-muted/70"
                onClick={(event) => {
                  event.stopPropagation()
                  onDelete(segment.segmentId)
                }}
                disabled={mutating}
                aria-label="删除单元"
              >
                <Trash2 className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>删除单元</TooltipContent>
          </Tooltip>
        </div>
        {dropIndicator === 'before' ? (
          <div className="absolute left-[-8px] top-0 h-full w-[3px] rounded-full bg-primary" />
        ) : null}
        {dropIndicator === 'after' ? (
          <div className="absolute right-[-8px] top-0 h-full w-[3px] rounded-full bg-primary" />
        ) : null}
      </div>
    )
  }
);

SegmentCard.displayName = 'SegmentCard';

export default SegmentCard;
